import React, { useMemo, useState } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Grid, Typography, Box, Divider, IconButton, Table, TableHead, TableRow, TableCell, TableBody } from '@mui/material';
import { Description, OpenInNew, PictureAsPdf, Delete, Send } from '@mui/icons-material';
import { Subcontractor } from '../../services/subcontractorService';
import { Project, getProjectById } from '../../services/projectService';
import GeneratePocrDialog, { PocrFormValues } from './GeneratePocrDialog';
import GeneratePokrDialog from './GeneratePokrDialog';
import protocolService from '../../services/protocolService';
import api from '../../services/api';
import ProtocolEmailDialog from './ProtocolEmailDialog';

interface SubcontractorDetailsDialogProps {
	open: boolean;
	onClose: () => void;
	subcontractor: Subcontractor | null;
	onProtocolsUpdated?: () => void;
}

const fieldLabelStyle = { color: '#666', fontSize: '0.75rem' } as const;
const fieldValueStyle = { fontWeight: 500, fontSize: '0.9rem' } as const;

const SubcontractorDetailsDialog: React.FC<SubcontractorDetailsDialogProps> = ({ open, onClose, subcontractor, onProtocolsUpdated }) => {
	// Tymczasowa lokalna lista dokumentów (na później podepniemy backend)
	const [documents, setDocuments] = useState<Array<{ 
		id: string; 
		type: 'POCR' | 'POKR' | 'WEZWANIE'; 
		number: string; 
		createdAt: string; 
		progress?: number; 
		value?: number; 
		pdfUrl?: string; 
		docUrl?: string; 
	}>>([]);
	const [loadingProtocols, setLoadingProtocols] = useState(false);
	const [openEmailDialog, setOpenEmailDialog] = useState(false);
	const [emailDialogData, setEmailDialogData] = useState<{ id: string | number; to: string; subject: string } | null>(null);

	// Zamiana linku webView na ID pliku
	const extractDriveId = (viewLink?: string): string | undefined => {
		if (!viewLink) return undefined;
		const byFileD = viewLink.match(/\/file\/d\/([a-zA-Z0-9_-]+)/);
		const byDocD = viewLink.match(/\/(?:document|spreadsheets|presentation)\/d\/([a-zA-Z0-9_-]+)/);
		const byPlainD = viewLink.match(/\/(?:u\/\d\/)?d\/([a-zA-Z0-9_-]+)/);
		const byQuery = viewLink.match(/[?&]id=([a-zA-Z0-9_-]+)/);
		return byFileD?.[1] || byDocD?.[1] || byPlainD?.[1] || byQuery?.[1];
	};

	const handleDownloadPdf = async (pdfUrl?: string, fallbackName?: string, docUrl?: string) => {
		try {
			let id = extractDriveId(pdfUrl);
			let res;
			if (id) {
				res = await api.get(`/drive/files/${id}/download`, { responseType: 'blob' });
			} else {
				id = extractDriveId(docUrl);
				if (!id) {
					console.error('Nie udało się wyodrębnić ID pliku z linków:', pdfUrl, docUrl);
					return;
				}
				res = await api.get(`/drive/files/${id}/export`, { params: { mimeType: 'application/pdf' }, responseType: 'blob' });
			}
			const blob = new Blob([res.data], { type: 'application/pdf' });
			const url = window.URL.createObjectURL(blob);
			const a = document.createElement('a');
			a.href = url;
			a.download = (fallbackName || 'plik') + '.pdf';
			document.body.appendChild(a);
			a.click();
			a.remove();
			window.URL.revokeObjectURL(url);
		} catch (e) {
			console.error('Nie udało się pobrać PDF:', e);
		}
	};

	const nextIndexForType = (t: 'POCR' | 'POKR' | 'WEZWANIE') => {
		return documents.filter(d => d.type === t).length + 1;
	};

	const handleGenerate = (type: 'POCR' | 'POKR' | 'WEZWANIE') => {
		if (!subcontractor) return;
		const now = new Date();
		const id = `${type}-${now.getTime()}`;
		const idx = nextIndexForType(type);
		let number = '';
		if (type === 'WEZWANIE') {
			number = `W${idx}.${subcontractor.contract_number}`;
		} else {
			number = `${type}.${idx}.${subcontractor.contract_number}`;
		}
		setDocuments(prev => [{ 
			id, 
			type, 
			number, 
			createdAt: now.toISOString().slice(0, 10), 
			progress: type !== 'WEZWANIE' ? subcontractor.progress : undefined, 
			value: type !== 'WEZWANIE' ? subcontractor.contract_value : undefined, 
			pdfUrl: 'https://drive.google.com', 
			docUrl: 'https://docs.google.com' 
		}, ...prev]);
	};

	const title = useMemo(() => {
		if (!subcontractor) return 'Szczegóły podwykonawcy';
		return `Szczegóły: ${subcontractor.name}`;
	}, [subcontractor]);

	const pocrPokr = documents.filter(d => d.type === 'POCR' || d.type === 'POKR');
	const wezwania = documents.filter(d => d.type === 'WEZWANIE');

	// Pobranie projektu i sterowanie formularzami
	const [project, setProject] = useState<Project | null>(null);
	const [openPocr, setOpenPocr] = useState(false);
	const [openPokr, setOpenPokr] = useState(false);

	React.useEffect(() => {
		(async () => {
			if (subcontractor?.project_id) {
				try {
					const p = await getProjectById(subcontractor.project_id);
					setProject(p);
				} catch {}
			}
		})();
	}, [subcontractor?.project_id]);


	// Loader protokołów (używany w kilku miejscach)
	const loadProtocols = React.useCallback(async () => {
		if (!subcontractor?.project_id) return;
		setLoadingProtocols(true);
		try {
			const list: any = await protocolService.getByProject(subcontractor.project_id);
			let items: any[] = Array.isArray(list) ? list : (list?.data || []);
			// Pokaż tylko protokoły bieżącego podwykonawcy
			items = items.filter((it: any) => String(it.subcontractor_id) === String(subcontractor.id));
			const mapped = items.map((it: any) => ({
				id: String(it.id),
				type: it.type as 'POCR' | 'POKR',
				number: it.protocol_number || `${it.type}.${it.sequence_number}.${it.project_id}`,
				createdAt: (it.protocol_date || it.created_at || '').toString().slice(0, 10),
				progress: typeof it.progress_percent === 'number' ? it.progress_percent : Number(it.progress_percent || 0),
				value: typeof it.value_in_period === 'number' ? it.value_in_period : Number(it.value_in_period || 0),
				pdfUrl: it.generated_pdf_url || undefined,
				docUrl: it.generated_google_doc_url || undefined,
				gmail_message_id: it.gmail_message_id || undefined,
				gmail_draft_id: it.gmail_draft_id || undefined,
				sequence: Number(it.sequence_number || 0)
			}));
			setDocuments(mapped);
		} catch (e) {
			console.error('Nie udało się pobrać protokołów:', e);
		} finally {
			setLoadingProtocols(false);
		}
	}, [subcontractor?.project_id, subcontractor?.id]);

	// Pobieranie protokołów przy otwarciu
	React.useEffect(() => {
		if (!open) return;
		loadProtocols();
	}, [open, loadProtocols]);

	const handleOpenSendEmail = (doc: { id: string | number; number: string }) => {
		if (!subcontractor) return;
		const subject = `${(project as any)?.name || (project as any)?.project_name || ''}: Protokół odbioru robót nr ${doc.number}`;
		setEmailDialogData({ id: doc.id, to: subcontractor.email || '', subject });
		setOpenEmailDialog(true);
	};

	const getSendIconColor = (doc: any) => {
		if (doc.gmail_message_id) return '#2e7d32'; // zielony – wysłane
		if (doc.gmail_draft_id) return '#f9a825'; // żółty – draft
		return '#d32f2f'; // czerwony – brak
	};

	const handlePocrSubmit = async (_values: PocrFormValues) => {
		setOpenPocr(false);
		await loadProtocols(); // odśwież listę natychmiast po utworzeniu
		try { onProtocolsUpdated && onProtocolsUpdated(); } catch {}
	};

	return (<>
		<Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
			<DialogTitle>{title}</DialogTitle>
			<DialogContent>
				{!subcontractor ? (
					<Typography variant="body2">Brak danych podwykonawcy.</Typography>
				) : (
					<Box>
						<Grid container spacing={2}>
							<Grid item xs={12} md={6}>
								<Typography sx={fieldLabelStyle}>Numer umowy</Typography>
								<Typography sx={fieldValueStyle}>{subcontractor.contract_number}</Typography>
							</Grid>
							<Grid item xs={12} md={6}>
								<Typography sx={fieldLabelStyle}>TWU</Typography>
								<Typography sx={fieldValueStyle}>{subcontractor.completion_date || '-'}</Typography>
							</Grid>

							<Grid item xs={12} md={6}>
								<Typography sx={fieldLabelStyle}>Nazwa podwykonawcy</Typography>
								<Typography sx={fieldValueStyle}>{subcontractor.name}</Typography>
							</Grid>
							<Grid item xs={12} md={6}>
								<Typography sx={fieldLabelStyle}>Wartość umowy</Typography>
								<Typography sx={fieldValueStyle}>{new Intl.NumberFormat('pl-PL', { style: 'currency', currency: 'PLN' }).format(subcontractor.contract_value)}</Typography>
							</Grid>

							<Grid item xs={12} md={6}>
								<Typography sx={fieldLabelStyle}>E-mail</Typography>
								<Typography sx={fieldValueStyle}>{subcontractor.email || '-'}</Typography>
							</Grid>

							<Grid item xs={12} md={6}>
								<Typography sx={fieldLabelStyle}>Telefon</Typography>
								<Typography sx={fieldValueStyle}>{subcontractor.phone || '-'}</Typography>
							</Grid>

							<Grid item xs={12}>
								<Typography sx={fieldLabelStyle}>Przedmiot umowy</Typography>
								<Typography sx={{ ...fieldValueStyle, whiteSpace: 'pre-wrap' }}>{subcontractor.contract_subject}</Typography>
							</Grid>

							<Grid item xs={12}>
								<Divider sx={{ my: 1 }} />
								<Typography variant="subtitle2" sx={{ mb: 1 }}>Generowanie dokumentów</Typography>
								<Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
									<Button variant="contained" size="small" onClick={() => setOpenPocr(true)}>Generuj POCR</Button>
									<Button variant="contained" size="small" onClick={() => setOpenPokr(true)}>Generuj POKR</Button>
									<Button variant="outlined" size="small" onClick={() => handleGenerate('WEZWANIE')}>Generuj wezwanie</Button>
								</Box>
							</Grid>

							<Grid item xs={12}>
								<Divider sx={{ my: 1 }} />
								<Typography variant="subtitle2" sx={{ mb: 1 }}>PROTOKOŁY ODBIORU ROBÓT</Typography>
								{loadingProtocols ? (
									<Typography variant="body2" color="text.secondary">Ładowanie…</Typography>
								) : pocrPokr.length === 0 ? (
									<Typography variant="body2" color="text.secondary">Brak protokołów.</Typography>
								) : (
									<Table size="small" sx={{ '& .MuiTableCell-root': { fontSize: '0.8rem' } }}>
										<TableHead>
											<TableRow>
												<TableCell>Nr</TableCell>
												<TableCell>Data utworzenia</TableCell>
												<TableCell align="center">Zaawansowanie</TableCell>
												<TableCell align="right">Wartość</TableCell>
												<TableCell align="center">PDF</TableCell>
												<TableCell align="center">G.Doc</TableCell>
												<TableCell align="center">Wyślij</TableCell>
												<TableCell align="center">Usuń</TableCell>
											</TableRow>
										</TableHead>
										<TableBody>
											{pocrPokr.map((doc: any) => (
												<TableRow key={doc.id}>
													<TableCell>{doc.number}</TableCell>
													<TableCell>{doc.createdAt}</TableCell>
													<TableCell align="center">{doc.progress !== undefined ? `${doc.progress}%` : '-'}</TableCell>
													<TableCell align="right">{doc.value !== undefined ? new Intl.NumberFormat('pl-PL', { style: 'currency', currency: 'PLN' }).format(doc.value) : '-'}</TableCell>
													<TableCell align="center">
														<IconButton size="small" component="a" href={doc.pdfUrl} target="_blank" rel="noreferrer">
															<PictureAsPdf fontSize="small" />
														</IconButton>
													</TableCell>
													<TableCell align="center">
														<IconButton size="small" component="a" href={doc.docUrl} target="_blank" rel="noreferrer">
															<Description fontSize="small" />
														</IconButton>
													</TableCell>
													<TableCell align="center">
														<IconButton size="small" onClick={() => handleOpenSendEmail({ id: doc.id, number: doc.number })}>
															<Send fontSize="small" htmlColor={getSendIconColor(doc)} />
														</IconButton>
													</TableCell>
													<TableCell align="center">
														<IconButton size="small" onClick={async () => {
															if (!window.confirm('Na pewno usunąć protokół?')) return;
															try { await protocolService.delete(doc.id); await loadProtocols(); } catch (e) { console.error('Błąd usuwania protokołu', e); }
														}}>
															<Delete fontSize="small" />
														</IconButton>
													</TableCell>
												</TableRow>
											))}
										</TableBody>
									</Table>
								)}
							</Grid>

							<Grid item xs={12}>
								<Divider sx={{ my: 1 }} />
								<Typography variant="subtitle2" sx={{ mb: 1 }}>Wezwania</Typography>
								{wezwania.length === 0 ? (
									<Typography variant="body2" color="text.secondary">Brak wezwań.</Typography>
								) : (
									<Table size="small" sx={{ '& .MuiTableCell-root': { fontSize: '0.8rem' } }}>
										<TableHead>
											<TableRow>
												<TableCell>Nr</TableCell>
												<TableCell>Data utworzenia</TableCell>
												<TableCell align="center">PDF</TableCell>
												<TableCell align="center">G.Doc</TableCell>
											</TableRow>
										</TableHead>
										<TableBody>
											{wezwania.map(doc => (
												<TableRow key={doc.id}>
													<TableCell>{doc.number}</TableCell>
													<TableCell>{doc.createdAt}</TableCell>
													<TableCell align="center">
														<IconButton size="small" component="a" href={doc.pdfUrl} target="_blank" rel="noreferrer">
															<PictureAsPdf fontSize="small" />
														</IconButton>
													</TableCell>
													<TableCell align="center">
														<IconButton size="small" component="a" href={doc.docUrl} target="_blank" rel="noreferrer">
															<Description fontSize="small" />
														</IconButton>
													</TableCell>
												</TableRow>
											))}
										</TableBody>
									</Table>
								)}
							</Grid>
						</Grid>
					</Box>
				)}
			</DialogContent>
			<DialogActions>
				<Button onClick={onClose}>Zamknij</Button>
			</DialogActions>
		</Dialog>

		{openEmailDialog && emailDialogData && (
			<ProtocolEmailDialog
				open={openEmailDialog}
				onClose={() => setOpenEmailDialog(false)}
				protocolId={emailDialogData.id}
				defaultTo={emailDialogData.to}
				defaultSubject={emailDialogData.subject}
				attachmentName={documents.find(d=>String(d.id)===String(emailDialogData.id))?.number + '.pdf'}
				attachmentUrl={documents.find(d=>String(d.id)===String(emailDialogData.id))?.pdfUrl}
				onSent={() => loadProtocols()}
				onDraft={() => loadProtocols()}
			/>
		)}

		{openPocr && subcontractor && (
			<GeneratePocrDialog
				open={openPocr}
				onClose={() => setOpenPocr(false)}
				subcontractor={subcontractor}
				project={project}
				onSubmit={handlePocrSubmit}
			/>
		)}

		{openPokr && subcontractor && (
			<GeneratePokrDialog
				open={openPokr}
				onClose={() => setOpenPokr(false)}
				subcontractor={subcontractor}
				project={project}
				onSubmit={async () => { setOpenPokr(false); await loadProtocols(); try { onProtocolsUpdated && onProtocolsUpdated(); } catch {} }}
			/>
		)}
	</>);
};

export default SubcontractorDetailsDialog;
